import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body content describing the action being confirmed. */
  description?: ReactNode;
  /** Optional leading icon (inherit its colour from `tone`). */
  icon?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual emphasis for the confirm button and icon. */
  tone?: 'primary' | 'danger';
  /** Disables the buttons and shows a spinner on the confirm button. */
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Reusable in-app confirmation dialog. Replaces the browser's native
 * confirm/alert popups with a modern, theme-consistent modal.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  icon,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  if (!open) return null;

  const toneIcon = tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary';
  const toneButton = tone === 'danger' ? 'bg-danger hover:bg-danger-dark' : 'bg-primary hover:bg-primary-dark';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-3">
          <div className="flex items-start gap-3">
            {icon && <div className={`p-2 rounded-lg shrink-0 ${toneIcon}`}>{icon}</div>}
            <div className="space-y-1 min-w-0">
              <h3 className="text-lg font-semibold text-text">{title}</h3>
              {description && <div className="text-sm text-text-muted">{description}</div>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 ${toneButton}`}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
